import React from "react";
import type { SiteAgentDetailDto } from "../models/edgeMonitorAdminModels";

const FONT_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";

const COLORS = {
  green: "#2D8C4E",
  terracotta: "#D4572B",
  primaryText: "#1A1A1A",
  dimText: "#6B6B6B",
};

export interface Zone1SiteAgentDetailProps {
  data: SiteAgentDetailDto | null | undefined;
  loadingNoData: boolean;
  notAvailable: boolean;
}

const parseUtcLoose = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const ms = Date.parse(hasTz ? iso : iso + "Z");
  return Number.isNaN(ms) ? null : ms;
};

const relativeAgo = (iso: string | null | undefined): string => {
  const t = parseUtcLoose(iso);
  if (t === null) return "—";
  const ms = Date.now() - t;
  if (ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
};

const formatTime = (iso: string | null | undefined): string => {
  const t = parseUtcLoose(iso);
  if (t === null) return "—";
  try {
    return new Date(t).toISOString().slice(11, 19) + "Z";
  } catch {
    return "—";
  }
};

const Row: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <div
    style={{
      display: "flex",
      gap: "8px",
      alignItems: "baseline",
      fontFamily: FONT_MONO,
      fontSize: "11px",
      lineHeight: "16px",
    }}
  >
    <span
      style={{
        color: COLORS.dimText,
        letterSpacing: "0.04em",
        minWidth: "62px",
        textTransform: "uppercase",
        fontSize: "10px",
      }}
    >
      {k}
    </span>
    <span style={{ color: COLORS.primaryText, wordBreak: "break-all" }}>{children}</span>
  </div>
);

const Zone1SiteAgentDetail: React.FC<Zone1SiteAgentDetailProps> = ({
  data,
  loadingNoData,
  notAvailable,
}) => {
  if (loadingNoData) {
    return (
      <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>
        Loading agent…
      </div>
    );
  }
  if (notAvailable && !data) {
    return (
      <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>
        no access
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLORS.dimText }}>—</div>
    );
  }

  const isActiveColor = data.isActive ? COLORS.green : COLORS.terracotta;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: "10px",
          letterSpacing: "0.1em",
          color: COLORS.dimText,
          textTransform: "uppercase",
          marginBottom: "4px",
        }}
      >
        SiteAgent · {data.name}
      </div>
      <Row k="Active">
        <span style={{ color: isActiveColor, fontWeight: 600 }}>
          {data.isActive ? "yes" : "no"}
        </span>
      </Row>
      <Row k="Heartbeat">
        {relativeAgo(data.lastHeartbeat)} · {formatTime(data.lastHeartbeat)}
      </Row>
      <Row k="Updated">
        {relativeAgo(data.updatedAt)} · {formatTime(data.updatedAt)}
      </Row>
      <Row k="Version">{data.agentVersion ?? "—"}</Row>
      <Row k="Hub conn">
        {/* Already 8-char truncated server-side per SEC-REQ-3. Render verbatim. */}
        {data.agentHubConnectionId ?? <span style={{ color: COLORS.dimText }}>—</span>}
      </Row>
    </div>
  );
};

export default React.memo(
  Zone1SiteAgentDetail,
  (a, b) =>
    a.loadingNoData === b.loadingNoData &&
    a.notAvailable === b.notAvailable &&
    a.data === b.data
);
